import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { applyQuoteToItems } from '../utils/cartPricing';

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [deliverySlot, setDeliverySlot] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const trackTimerRef = useRef(null);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // Track cart snapshot for abandoned cart analytics (debounced)
  useEffect(() => {
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user?.admin_id && user?.id) {
          axios.post(`${API}/track/cart-snapshot`, {
            customer_id: user.id,
            admin_id: user.admin_id,
            session_id: sessionStorage.getItem('_sid') || '',
            items: items.map(i => ({ product_id: i.product_id, product_name: i.product_name, price: i.price, quantity: i.quantity })),
          }).catch(() => {});
        }
      } catch { /* tracking is best-effort */ }
    }, 3000);
    return () => { if (trackTimerRef.current) clearTimeout(trackTimerRef.current); };
  }, [items]);

  const addItem = (product, quantity = 1, selectedVariant = null) => {
    setItems(prevItems => {
      // Cart key includes variant label for variant-aware uniqueness
      const cartKey = selectedVariant
        ? `${product.id}__${selectedVariant.label}`
        : product.id;
      const existingIndex = prevItems.findIndex(item => item.cart_key === cartKey);
      
      const price = selectedVariant ? selectedVariant.price : product.price;
      const mrp = selectedVariant ? selectedVariant.mrp : product.mrp;
      const unit = selectedVariant ? selectedVariant.label : product.unit;
      
      if (existingIndex >= 0) {
        const newItems = [...prevItems];
        newItems[existingIndex].quantity += quantity;
        newItems[existingIndex].total = newItems[existingIndex].quantity * price;
        return newItems;
      }
      
      return [...prevItems, {
        cart_key: cartKey,
        product_id: product.id,
        product_name: product.name,
        product_image: product.image_url || (product.images && product.images[0]) || '',
        unit,
        price,
        mrp,
        variant_label: selectedVariant ? selectedVariant.label : null,
        quantity,
        total: price * quantity
      }];
    });
  };

  const removeItem = (cartKey) => {
    setItems(prevItems => prevItems.filter(item => (item.cart_key || item.product_id) !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeItem(cartKey);
      return;
    }
    
    setItems(prevItems => prevItems.map(item => {
      if ((item.cart_key || item.product_id) === cartKey) {
        return {
          ...item,
          quantity,
          total: item.price * quantity
        };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setItems([]);
    setDeliverySlot(null);
    setDeliveryDate(null);
  };

  // Replace stale cart prices with the server's (from POST /customer/cart/quote
  // or a PRICE_CHANGED response). Stable identity, and a no-op when nothing
  // changed, so it is safe to call on every quote.
  const repriceItems = useCallback((lines) => {
    setItems(prevItems => applyQuoteToItems(prevItems, lines));
  }, []);

  const getItemQuantity = (productId, variantLabel = null) => {
    const cartKey = variantLabel ? `${productId}__${variantLabel}` : productId;
    const item = items.find(item => (item.cart_key || item.product_id) === cartKey);
    return item ? item.quantity : 0;
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = items.reduce((sum, item) => sum + item.total, 0);
  const totalSavings = items.reduce((sum, item) => sum + ((item.mrp - item.price) * item.quantity), 0);

  return (
    <CartContext.Provider
      value={{
        items,
        deliverySlot,
        deliveryDate,
        setDeliverySlot,
        setDeliveryDate,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        repriceItems,
        getItemQuantity,
        totalItems,
        totalAmount,
        totalSavings,
        isEmpty: items.length === 0
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
